import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBan,
  IconCheck,
  IconPrinter,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { unitsOfMeasureApi } from '../api/catalogs';
import { counterpartiesApi } from '../api/counterparties';
import { employeesApi, hasPassportData } from '../api/employees';
import { nomenclatureApi } from '../api/nomenclature';
import { organizationsApi } from '../api/organizations';
import {
  DEFAULT_VALIDITY_DAYS,
  POA_STATUS_COLORS,
  POA_STATUS_LABELS,
  powersOfAttorneyApi,
  type PowerOfAttorneyInput,
} from '../api/powers-of-attorney';
import {
  emptyPoaLineRow,
  PowerOfAttorneyLinesEditor,
  type PoaLineRow,
} from '../components/PowerOfAttorneyLinesEditor';
import { useAuth } from '../auth/AuthContext';

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function todayInput(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function addDaysInput(dateInput: string, days: number): string {
  if (!dateInput) return '';
  const date = new Date(`${dateInput}T00:00:00`);
  date.setDate(date.getDate() + days);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

export function PowerOfAttorneyFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [organizationId, setOrganizationId] = useState('');
  const [date, setDate] = useState(isNew ? todayInput() : '');
  const [validUntil, setValidUntil] = useState(isNew ? addDaysInput(todayInput(), DEFAULT_VALIDITY_DAYS) : '');
  const [employeeId, setEmployeeId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [basisDocName, setBasisDocName] = useState('');
  const [basisDocNumber, setBasisDocNumber] = useState('');
  const [basisDocDate, setBasisDocDate] = useState('');
  const [comment, setComment] = useState('');
  const [lines, setLines] = useState<PoaLineRow[]>([emptyPoaLineRow()]);
  const [formError, setFormError] = useState<string[] | null>(null);

  const [executeOpen, setExecuteOpen] = useState(false);
  const [executedDocument, setExecutedDocument] = useState('');

  const loadedFor = useRef<string | null>(null);

  const poaQuery = useQuery({
    queryKey: ['power-of-attorney', id],
    queryFn: () => powersOfAttorneyApi.get(id!),
    enabled: !!id && !isNew,
  });

  const organizations = useQuery({ queryKey: ['organizations'], queryFn: () => organizationsApi.list() });
  const employees = useQuery({ queryKey: ['employees', 'all'], queryFn: () => employeesApi.list({ pageSize: 500 }) });
  const counterparties = useQuery({
    queryKey: ['counterparties', 'all'],
    queryFn: () => counterpartiesApi.list({ pageSize: 500 }),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const nomenclature = useQuery({
    queryKey: ['nomenclature', 'all'],
    queryFn: () => nomenclatureApi.list({ pageSize: 500 }),
  });

  const poa = poaQuery.data?.powerOfAttorney ?? null;
  // Отоваренную и отозванную доверенность правкам не подлежит: бланк уже на руках
  const canEditDocument = canEditGlobally && (isNew || (poa !== null && poa.status !== 'USED' && poa.status !== 'REVOKED'));

  // Одна организация — подставляем сразу, выбирать не из чего
  useEffect(() => {
    if (!isNew || organizationId) return;
    const items = organizations.data?.items ?? [];
    if (items.length === 1) setOrganizationId(items[0]!.id);
  }, [isNew, organizationId, organizations.data]);

  // Заполняем форму один раз при загрузке — иначе фоновый рефетч перезаписал бы
  // то, что пользователь уже успел набрать.
  useEffect(() => {
    if (!poa || loadedFor.current === poa.id) return;
    loadedFor.current = poa.id;

    setOrganizationId(poa.organizationId);
    setDate(toDateInput(poa.date));
    setValidUntil(toDateInput(poa.validUntil));
    setEmployeeId(poa.employeeId);
    setCounterpartyId(poa.counterpartyId);
    setBasisDocName(poa.basisDocName ?? '');
    setBasisDocNumber(poa.basisDocNumber ?? '');
    setBasisDocDate(toDateInput(poa.basisDocDate));
    setComment(poa.comment ?? '');
    setLines(
      poa.lines.map((l) => ({
        key: l.id,
        nomenclatureId: l.nomenclatureId,
        name: l.name,
        unitId: l.unitId ?? '',
        quantity: l.quantity,
      })),
    );
  }, [poa]);

  const employeeItems = employees.data?.items ?? [];
  const selectedEmployee = employeeItems.find((e) => e.id === employeeId) ?? null;

  const organizationOptions = (organizations.data?.items ?? []).map((o) => ({ value: o.id, label: o.shortName }));
  const employeeOptions = employeeItems.map((e) => ({
    value: e.id,
    label: e.position ? `${e.fullName} — ${e.position}` : e.fullName,
  }));
  const counterpartyOptions = (counterparties.data?.items ?? []).map((c) => ({ value: c.id, label: c.shortName }));

  function buildPayload(): PowerOfAttorneyInput {
    return {
      organizationId,
      date,
      validUntil,
      employeeId,
      counterpartyId,
      basisDocName: basisDocName || null,
      basisDocNumber: basisDocNumber || null,
      basisDocDate: basisDocDate || null,
      comment: comment || null,
      lines: lines.map((l) => ({
        nomenclatureId: l.nomenclatureId,
        name: l.name.trim(),
        unitId: l.unitId || null,
        quantity: l.quantity,
      })),
    };
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      return isNew ? powersOfAttorneyApi.create(payload) : powersOfAttorneyApi.update(id!, payload);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      notifications.show({ color: 'green', message: 'Доверенность сохранена' });
      if (isNew) {
        navigate(`/powers-of-attorney/${result.powerOfAttorney.id}`, { replace: true });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['power-of-attorney', id] });
      }
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (!organizationId) errors.push('Укажите организацию');
    if (!date) errors.push('Укажите дату выдачи');
    if (!validUntil) errors.push('Укажите срок действия');
    if (date && validUntil && validUntil < date) errors.push('Срок действия не может быть раньше даты выдачи');
    if (!employeeId) errors.push('Укажите сотрудника');
    if (!counterpartyId) errors.push('Укажите поставщика');
    if (selectedEmployee && !hasPassportData(selectedEmployee)) {
      errors.push('У сотрудника не заполнены паспортные данные — доверенность не будет действительна');
    }
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.quantity.trim()) errors.push(`Строка ${i + 1}: укажите количество`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate();
  }

  const executeMutation = useMutation({
    mutationFn: () => powersOfAttorneyApi.execute(id!, executedDocument),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['power-of-attorney', id] });
      void queryClient.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      notifications.show({ color: 'green', message: 'Отметка о выполнении сохранена' });
      setExecuteOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось сохранить отметку' });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => powersOfAttorneyApi.revoke(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['power-of-attorney', id] });
      void queryClient.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      notifications.show({ color: 'green', message: 'Доверенность отозвана' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось отозвать' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => powersOfAttorneyApi.remove(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['powers-of-attorney'] });
      notifications.show({ color: 'green', message: 'Доверенность удалена' });
      navigate('/powers-of-attorney');
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось удалить' });
    },
  });

  if (!isNew && !poa) {
    return <Text c="dimmed">Загрузка…</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/powers-of-attorney')}
          >
            К списку
          </Button>
          <Title order={2}>{isNew ? 'Новая доверенность' : `Доверенность № ${poa!.number}`}</Title>
          {poa && (
            <Badge color={POA_STATUS_COLORS[poa.status]} variant="light">
              {POA_STATUS_LABELS[poa.status]}
            </Badge>
          )}
        </Group>

        {poa && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              component="a"
              href={`/powers-of-attorney/${poa.id}/print`}
              target="_blank"
            >
              Печать
            </Button>
            {canEditGlobally && poa.status === 'ACTIVE' && (
              <Button variant="light" leftSection={<IconCheck size={16} />} onClick={() => setExecuteOpen(true)}>
                Отметить получение
              </Button>
            )}
            {canEditGlobally && poa.status !== 'USED' && poa.status !== 'REVOKED' && (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconBan size={16} />}
                onClick={() => {
                  if (window.confirm(`Отозвать доверенность № ${poa.number}?`)) revokeMutation.mutate();
                }}
                loading={revokeMutation.isPending}
              >
                Отозвать
              </Button>
            )}
            {canEditGlobally && poa.status !== 'USED' && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={() => {
                  if (window.confirm(`Удалить доверенность № ${poa.number}? Действие необратимо.`)) {
                    removeMutation.mutate();
                  }
                }}
                loading={removeMutation.isPending}
              >
                Удалить
              </Button>
            )}
          </Group>
        )}
      </Group>

      {poa?.status === 'USED' && (
        <Alert color="blue" variant="light">
          Ценности получены по документу «{poa.executedDocument}»
          {poa.executedAt ? ` — ${new Date(poa.executedAt).toLocaleDateString('ru-RU')}` : ''}
        </Alert>
      )}

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Group grow align="flex-start">
        <Select
          label="Организация"
          placeholder="Выберите организацию"
          data={organizationOptions}
          value={organizationId || null}
          onChange={(v) => setOrganizationId(v ?? '')}
          // Организацию выписанной доверенности менять нельзя: номер выдан
          // нумератором конкретного юрлица
          disabled={!canEditDocument || !isNew}
          required
          searchable
        />
        <TextInput
          label="Дата выдачи"
          type="date"
          value={date}
          onChange={(e) => {
            const next = e.currentTarget.value;
            setDate(next);
            // Срок тянется за датой, пока его не правили руками
            if (isNew) setValidUntil(addDaysInput(next, DEFAULT_VALIDITY_DAYS));
          }}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Действительна по"
          type="date"
          value={validUntil}
          onChange={(e) => setValidUntil(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
      </Group>

      <Group grow align="flex-start">
        <Stack gap={4}>
          <Select
            label="Кому выдана"
            placeholder="Выберите сотрудника"
            data={employeeOptions}
            value={employeeId || null}
            onChange={(v) => setEmployeeId(v ?? '')}
            disabled={!canEditDocument}
            required
            searchable
          />
          {selectedEmployee && !hasPassportData(selectedEmployee) && (
            <Text size="xs" c="orange">
              У сотрудника не заполнены паспортные данные — заполните карточку в справочнике «Сотрудники»
            </Text>
          )}
          {selectedEmployee && hasPassportData(selectedEmployee) && (
            <Text size="xs" c="dimmed">
              Паспорт {selectedEmployee.passportSeries} {selectedEmployee.passportNumber}
            </Text>
          )}
        </Stack>
        <Select
          label="Получить от (поставщик)"
          placeholder="Выберите контрагента"
          data={counterpartyOptions}
          value={counterpartyId || null}
          onChange={(v) => setCounterpartyId(v ?? '')}
          disabled={!canEditDocument}
          required
          searchable
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label="Документ-основание"
          placeholder="Счёт на оплату, договор, накладная…"
          value={basisDocName}
          onChange={(e) => setBasisDocName(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Номер документа"
          value={basisDocNumber}
          onChange={(e) => setBasisDocNumber(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Дата документа"
          type="date"
          value={basisDocDate}
          onChange={(e) => setBasisDocDate(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <Stack gap={4}>
        <Text size="sm" fw={600}>
          Перечень ценностей, подлежащих получению
        </Text>
        <PowerOfAttorneyLinesEditor
          lines={lines}
          onChange={setLines}
          canEdit={!!canEditDocument}
          nomenclatureOptions={nomenclature.data?.items ?? []}
          units={units.data?.items ?? []}
        />
      </Stack>

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            {isNew ? 'Выписать доверенность' : 'Сохранить'}
          </Button>
        </Group>
      )}

      <Modal opened={executeOpen} onClose={() => setExecuteOpen(false)} title="Отметка о выполнении">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Укажите документ, по которому ценности фактически получены — так же, как в журнале учёта
            доверенностей. После отметки доверенность считается отоваренной.
          </Text>
          <TextInput
            label="Документ получения"
            placeholder="УПД № 15 от 12.08.2026"
            value={executedDocument}
            onChange={(e) => setExecutedDocument(e.currentTarget.value)}
            required
          />
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={() => executeMutation.mutate()}
            loading={executeMutation.isPending}
            disabled={!executedDocument.trim()}
          >
            Сохранить отметку
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
